import React, { useMemo } from "react";
import { SECTION_HEADING } from "../utils/homeType";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import HelpMark from "./HelpMark";
import { usePickThree } from "../hooks/usePickThree";
import { usePickThreeStanding } from "../hooks/usePickThreeStanding";
import {
  buildPickRows, describeWindow, GAME_NAME, inviteText, LOCK_RULE, lockAtFor, pickWeek, scoreEntry, scoreLine, standingBand, weekWindow,
} from "../games/pickThree";
import { buildGamesBySlug } from "../utils/gameStatus";
import { useLockClock } from "./PickThreeList";
import { InviteButton, PickThreeBackdrop, Slots } from "./PickThreeParts";

// The weekly game on /home, under the daily games. One card for the lineup
// week, whose face follows the week:
//
//   before the schedule is in   nothing to pick from yet
//   open                        empty slots or the picks made, the next lock and
//                               the last; picks whose games have started show
//                               a lock and their points
//   live                        locked, with points as games are scored
//   final                       the score against the best possible three
//
// Playing takes an account. In the demo the card opens /pick-three, the public
// page where a visitor chooses three teams before signing up. The picker and
// the result are on /games/pick-three.

const PILL = { display: "inline-block", fontSize: 12, fontWeight: 700, padding: "4px 10px", borderRadius: 999 };
const PILLS = {
  open: { label: "Open", color: "#7dffa3", background: "rgba(125,255,163,0.14)" },
  in: { label: "Picks in", color: "#7dffa3", background: "rgba(125,255,163,0.14)" },
  live: { label: "Live", color: "#ff9b94", background: "rgba(255,155,148,0.16)" },
  final: { label: "Final", color: "#fff", background: "rgba(255,255,255,0.16)" },
  soon: { label: "Soon", color: "#fff", background: "rgba(255,255,255,0.16)" },
};

const BUTTON = {
  display: "block", width: "100%", marginTop: 12, padding: "13px 0", border: "none", borderRadius: 50,
  fontSize: 14, fontWeight: 700, cursor: "pointer",
};
const SOLID = { ...BUTTON, backgroundColor: "#fff", color: "#071F39" };
const OUTLINE = { ...BUTTON, backgroundColor: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.7)" };

function Line({ label, value }) {
  return (
    <div style={{ fontSize: 13, lineHeight: 1.5, color: "rgba(255,255,255,0.85)" }}>
      <b style={{ color: "#fff" }}>{label}</b> {value}
    </div>
  );
}

function WeeklyGameSection({ demo = false }) {
  const navigate = useNavigate();
  const { teams: bootTeams, seasonConfig } = useAuth();
  const week = pickWeek(seasonConfig?.currentWeek);
  // Live points while games are on; the boot map in the demo, which loads nothing.
  const { rows, entries, teams: liveTeams } = usePickThree(demo ? null : week);
  const teams = demo ? bootTeams : liveTeams;

  // A minute is fine here: the card only shows lock badges.
  const now = useLockClock(60_000);
  const weekState = useMemo(() => (rows ? weekWindow(rows, teams, now) : null), [rows, teams, now]);
  const picks = useMemo(() => entries?.[week]?.teams || [], [entries, week]);
  const captain = entries?.[week]?.captain || null;
  const gamesBySlug = useMemo(() => buildGamesBySlug(rows || []), [rows]);
  // Picker rows for the invite's names and spreads.
  const rowsBySlug = useMemo(
    () => Object.fromEntries(buildPickRows(teams, gamesBySlug).map((r) => [r.slug, r])),
    [teams, gamesBySlug],
  );
  const lockedPicks = picks.filter((slug) => {
    const game = gamesBySlug[slug];
    const lockAt = game ? lockAtFor(game.date, game.startTimeTbd) : null;
    return Boolean(lockAt) && now >= lockAt;
  });
  const previous = entries?.[week - 1];
  const score = useMemo(
    () => (picks.length === 3 ? scoreEntry(teams, picks, week, captain) : null),
    [teams, picks, week, captain],
  );
  // Where the player finished, once the week has locked and they have picks.
  const locked = weekState?.phase === "live" || weekState?.phase === "final";
  const band = standingBand(usePickThreeStanding(week, Boolean(score) && locked));
  const lastWeek = useMemo(
    () => (previous?.teams?.length === 3 && week > 1 ? scoreEntry(teams, previous.teams, week - 1, previous.captain) : null),
    [teams, previous, week],
  );

  // Nothing before week 1 and nothing in the off-season.
  if (week === null) return null;

  const open = (to = "/games/pick-three") => navigate(to);
  const shell = (children) => (
    <div style={{ marginTop: 32 }} data-tour="home-weekly">
      <h2 style={{ ...SECTION_HEADING, marginBottom: 4 }}>
        Weekly Game
        <HelpMark step="home-weekly" label="How Pick Three works" demo={demo} />
      </h2>
      {/* What the game is for, in one line, before the card asks for picks.
          Daily Games needs no such line: its cards say what they are. */}
      <p style={{ fontSize: 13, color: "var(--color-text-muted)", margin: "0 0 10px", lineHeight: 1.4 }}>
        League not going your way? Think you know which teams will score the most this week? Pick three and prove it.
      </p>
      {/* The whole card opens the game, not only its button. A tap on one of
          the card's own buttons (invite, last week) does that button's job. */}
      <div
        onClick={(e) => { if (!e.target.closest("button")) open(demo ? "/pick-three" : undefined); }}
        style={{ cursor: "pointer", WebkitTapHighlightColor: "transparent" }}
      >
        <PickThreeBackdrop style={{ borderRadius: 18, padding: 16 }}>{children}</PickThreeBackdrop>
      </div>
    </div>
  );
  const header = (pill) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
      <span style={{ fontSize: 18, fontWeight: 800 }}>{GAME_NAME}</span>
      <span style={{ ...PILL, color: pill.color, backgroundColor: pill.background }}>{pill.label}</span>
    </div>
  );
  const about = `Pick the three FBS teams you think score the most Lineup points in week ${week}, and make one your captain. No league needed.`;

  if (demo) {
    return shell(
      <>
        {header({ ...PILLS.soon, label: `Week ${week}` })}
        <p style={{ fontSize: 13, lineHeight: 1.5, margin: "0 0 12px", color: "rgba(255,255,255,0.85)" }}>{about}</p>
        <Slots picks={[]} teams={teams} />
        <button type="button" onClick={() => open("/pick-three")} style={SOLID}>Make your picks</button>
      </>,
    );
  }

  if (!weekState || !entries) {
    return shell(
      <>
        {header(PILLS.soon)}
        <p style={{ fontSize: 13, margin: 0, color: "rgba(255,255,255,0.85)" }}>Loading week {week}.</p>
      </>,
    );
  }

  if (weekState.phase === "unavailable") {
    return shell(
      <>
        {header(PILLS.soon)}
        <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, color: "rgba(255,255,255,0.85)" }}>
          Week {week} opens once its schedule is in.
        </p>
      </>,
    );
  }

  const times = describeWindow(weekState);
  const lastWeekLine = lastWeek && weekState.phase === "open" && (
    <button
      type="button"
      onClick={() => open(`/games/pick-three?week=${week - 1}`)}
      style={{ display: "block", width: "100%", marginTop: 10, padding: 0, border: "none", background: "none", textAlign: "left", fontSize: 12, color: "rgba(255,255,255,0.8)", cursor: "pointer" }}
    >
      Last week: <b style={{ color: "#fff" }}>{scoreLine(lastWeek)}</b>. See result.
    </button>
  );

  if (weekState.phase === "open") {
    const hasPicks = picks.length === 3;
    const started = weekState.nextLockAt && weekState.kickoff && now >= weekState.kickoff.date;
    const barScores = hasPicks ? picks.map((slug, i) => (lockedPicks.includes(slug) ? score.rows[i] : undefined)) : null;
    const canChange = lockedPicks.length < picks.length;
    return shell(
      <>
        {header(hasPicks ? PILLS.in : PILLS.open)}
        {!hasPicks && (
          <p style={{ fontSize: 13, lineHeight: 1.5, margin: "0 0 12px", color: "rgba(255,255,255,0.85)" }}>
            {about}{started ? " Some games have started. You can still pick from the ones that have not." : ""}
          </p>
        )}
        <div style={{ margin: hasPicks ? "10px 0 12px" : 0 }}>
          <Slots picks={picks} teams={teams} scores={barScores} locked={lockedPicks} captain={captain} />
        </div>
        <div style={{ marginTop: 12 }}>
          {lockedPicks.length > 0
            ? <Line label="So far" value={`${score.total} points. Points update as games finish.`} />
            : <Line label={`Week ${week} starts`} value={times.starts} />}
          <Line label="Last picks" value={times.last} />
          <div style={{ marginTop: 4, fontSize: 12, lineHeight: 1.4, color: "rgba(255,255,255,0.75)" }}>{LOCK_RULE}</div>
        </div>
        {/* Saved before captains existed (migration 085): the entry still
            stands, undoubled, but a save now needs a captain. */}
        {hasPicks && !captain && canChange && (
          <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: "#FFCC00" }}>Choose a captain to double one team's points.</div>
        )}
        <button type="button" onClick={() => open()} style={hasPicks && captain ? OUTLINE : SOLID}>
          {!hasPicks ? "Make your picks" : !captain && canChange ? "Choose a captain" : canChange ? "Change picks" : "See your picks"}
        </button>
        {hasPicks && captain && <InviteButton text={inviteText(week, picks, captain, rowsBySlug)} variant="onDark" />}
        {lastWeekLine}
      </>,
    );
  }

  // Locked: live, or final.
  const isFinal = weekState.phase === "final";
  if (!score) {
    return shell(
      <>
        {header(isFinal ? PILLS.final : PILLS.live)}
        <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, color: "rgba(255,255,255,0.85)" }}>
          {isFinal
            ? `You did not make picks for week ${week}. Week ${week + 1} picks open Sunday at 10am ET.`
            : `Every week ${week} team has locked, and you did not make picks. A new week opens Sunday at 10am ET.`}
        </p>
      </>,
    );
  }

  return shell(
    <>
      {header(isFinal ? { ...PILLS.final, label: `Week ${week} final` } : PILLS.live)}
      <div style={{ margin: "10px 0 12px" }}><Slots picks={picks} teams={teams} scores={score.rows} captain={captain} /></div>
      <Line label={isFinal ? "Score" : "So far"} value={scoreLine(score)} />
      {band && <Line label="Standing" value={band.label} />}
      {!isFinal && <Line label="All picks locked." value="Points update as games finish." />}
      <button type="button" onClick={() => open()} style={OUTLINE}>See result</button>
    </>,
  );
}

export default WeeklyGameSection;
